import React, { Fragment } from 'react';

import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag, { type Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

import { env } from '@/env';

// Errors with no actionable source in our code, matched by message because that's their only signal:
// browser-extension DOM mutations (Google Translate's removeChild/insertBefore) and DOM walks
// (parentNode.getAttribute from react-select internals), injected scripts/globals (in-app-browser
// SCDynImageBridge, the `copy` devtools global), broken images, transient connectivity drops, and
// the reCAPTCHA widget's own timeout.
const IGNORED_ERROR_MESSAGES: RegExp[] = [
  /Failed to execute '(removeChild|insertBefore)' on 'Node'/,
  /\.parentNode\.getAttribute/,
  /SCDynImageBridge/,
  /copy is not defined/,
  /Failed to load the image/,
  // Transient connectivity drops (offline / request aborted by navigation). ASSUMPTION: real Gearbelt API
  // failures surface as typed ky HTTPError/TimeoutError handled at the call site, not as these generic
  // messages — so dropping them here doesn't hide our own regressions. Revisit if API failures go undetected.
  /Failed to fetch/,
  /Load failed/,
  /NetworkError when attempting to fetch resource/,
  /reCAPTCHA Timeout/,
];

function isIgnoredError(event: Event): boolean {
  const message = event.errors[0]?.errorMessage ?? '';
  return IGNORED_ERROR_MESSAGES.some((pattern) => pattern.test(message));
}

Bugsnag.start({
  apiKey: env.VITE_BUGSNAG_API_KEY ?? '',
  plugins: [new BugsnagPluginReact()],
  releaseStage: env.VITE_BUGSNAG_RELEASE_STAGE ?? 'local',
  onError: (event) => {
    if (isIgnoredError(event)) {
      return false;
    }
  },
});
BugsnagPerformance.start({ apiKey: env.VITE_BUGSNAG_API_KEY ?? '' });

const BugsnagPlugin = Bugsnag.getPlugin('react');

const ErrorBoundary = env.VITE_BUGSNAG_API_KEY ? (BugsnagPlugin ? BugsnagPlugin.createErrorBoundary(React) : Fragment) : Fragment;
export { ErrorBoundary };
